import React, { useState, useEffect, useRef, useLayoutEffect, useContext } from 'react'
import _ from 'lodash'
import ReactDOM from 'react-dom'
import { getDataset, copyDataset } from '../api'
import ButtonWithLoading from 'live/components/forms/ui/button-with-loading'
import FormGroup from 'live/components/forms/ui/form-group'
import { Field } from 'plugin-liverig/shared/asset/components/Fieldset'
import dateService from 'live/services/date'
import CustomDateTimeInput from '../components/custom-dt-format'
import DatePicker from 'react-datepicker'
import Select from 'react-select'
import i18n from 'live/services/i18n'
import LiveModal from 'live/components/ui/Modal'
import { HotTable } from '@handsontable/react'
import { HotContainer } from '../commom'
import moment from 'moment'
import globalNotification from 'live/services/global-notification'
import { DvDRow, emptyDvDDataset, addCalculatedDvDColumns, getElementForCell } from '../functions/functions'
import { StyledOverlay } from '../styles/styles'
import { LoadingSpinner } from 'plugin-wells/shared/components/LoadingSpinner'
import {
    Container,
    FormConfig,
    StyledLiveModal,
    InputContainerDatepickerStyled,
    ContainerDate,
    ContentBody
} from '../styles/styles'
import { numberFormat } from 'highcharts'

type CopyDataProps = {
    modalTitle?: string
    triggerFromRow?: Record<string, unknown>
    wellId: string
    setShow: Function
    getAllDatasetInfo: Function
    maxDate?: number
    setParentTableData: Function
}

class DvDCopyDataForm extends React.Component<CopyDataProps> {
    constructor(props) {
        super(props)
        this.state = {
            wells: [],
            isDropDownLoading: true,
            isDataLoading: false,
            copyWellId: undefined,
            copyWellName: undefined,
            copyStartDate: undefined,
            copyEndDate: undefined,
            copySideTrackDepth: undefined,
            tableRef: React.createRef<HotTable>(),
            tableData: addCalculatedDvDColumns(emptyDvDDataset),
            copyMinDate: 0,
            dataStats: undefined,
            isGoodInputs: false
        }
    }
    componentDidMount() {
        const controller = new AbortController()
        const signal = controller.signal

        return this.props
            .getAllDatasetInfo(signal, 'ds_day_depth', 'well')
            .then(result => result.json())
            .then(data => {
                var obj = []
                data.forEach(x => {
                    obj.push({
                        value: x?.assetId?.trim(),
                        label: x?.assetName?.trim()
                    })
                })
                obj = obj.filter(x => x.value != this.props.wellId)
                this.setState({ wells: obj, isDropDownLoading: false })
            })
    }

    closeModal = () => this.props.setShow(false)

    copyData = () => {
        const controller = new AbortController()
        const signal = controller.signal
        copyDataset(
            signal,
            this.props.wellId,
            this.state['copyWellId'],
            this.state['copyWellName'],
            this.state['copyStartDate'],
            this.state['copyEndDate']
        )
            .then(result => result.json())
            .then(response => {
                if (response.responseCode === 'OK') {
                    if (!response) return

                    if (response.result && response.result.data && response.result.data.length) {
                        this.props.setParentTableData(addCalculatedDvDColumns(response.result.data))
                    } else {
                        this.props.setParentTableData(addCalculatedDvDColumns(emptyDvDDataset))
                    }
                    globalNotification.success('The data has been copied successfully!')
                    this.closeModal()
                } else {
                    globalNotification.error(response.message ?? "We're having trouble copying the dataset")
                }
                this.setState({ isDataLoading: false })
            })
            .catch(e => {
                globalNotification.error(e.message ?? "We're having trouble copying the dataset")
                this.setState({ isDataLoading: false })
            })
            .finally(() => this.setState({ isDataLoading: false }))
    }

    getAndSetDataStats = () => {
        const data: Array<Record<string, string>> = this.state['tableData']
        const startTime: number = this.state['copyStartDate']
        const endTime: number = this.state['copyEndDate']
        const minTime: number = this.state['copyMinDate']
        const maxTime: number = this.props.maxDate

        let dataStats: Record<string, number> = { numberOfRows: 0, numberOfModifiedRows: 0, numberOfSelectedRows: 0 }

        if (data && data.length > 0) {
            dataStats = {
                numberOfRows: data.length,
                numberOfSelectedRows: 0,
                numberOfModifiedRows: 0,
                numberOfEligibleRows: 0
            }

            if (minTime && maxTime) {
                let clonedData = _.clone(data) as Array<Record<string, string>>
                clonedData = clonedData.filter(
                    x => parseInt(x['timestamp']) >= minTime && parseInt(x['timestamp']) < maxTime
                )
                dataStats['numberOfEligibleRows'] = clonedData.length
            }
        }

        if (startTime && endTime && data && data.length > 0) {
            try {
                let clonedData = _.clone(data) as Array<Record<string, string>>
                clonedData = clonedData.filter(
                    x => parseInt(x['timestamp']) >= startTime && parseInt(x['timestamp']) <= endTime
                )
                dataStats['numberOfSelectedRows'] = clonedData.length
                clonedData = clonedData.filter(x => x && x['changeLog'] && x['changeLog']['changeBy'] !== 'System')
                dataStats['numberOfModifiedRows'] = clonedData.length
            } catch {
                dataStats = {
                    numberOfRows: 0,
                    numberOfSelectedRows: 0,
                    numberOfModifiedRows: 0
                }
            }
        }
        this.setState({ dataStats: dataStats })
    }

    getDataStatsAsString = () => {
        const data: Record<string, string> = this.state['dataStats']
        if (data && Object.keys(data).length > 0) {
            return `Total Row(s) : ${data['numberOfRows']} / Copy Eligible Row(s) : ${data['numberOfEligibleRows']} / Selected Row(s) : ${data['numberOfSelectedRows']} / Modified Row(s) : ${data['numberOfModifiedRows']}`
        }
        return ``
    }

    validateInputs = () => {
        const startTime: number = this.state['copyStartDate']
        const endTime: number = this.state['copyEndDate']
        const minTime: number = this.state['copyMinDate']
        const maxTime: number = this.props.maxDate
        const wellId: string = this.state['copyWellId']

        if (
            wellId &&
            startTime &&
            endTime &&
            endTime > startTime &&
            endTime != 0 &&
            startTime >= minTime &&
            endTime < maxTime
        ) {
            this.setState({ isGoodInputs: true })
        }
    }

    onChangeWellSelect = e => {
        this.onChangeFilterParameters('', 'copyWellId', e.value)
        this.onChangeFilterParameters('', 'copyWellName', e.label)
        const controller = new AbortController()
        const signal = controller.signal
        this.setState({
            isDataLoading: true,
            copyStartDate: undefined,
            copyEndDate: undefined,
            copySideTrackDepth: undefined
        })
        getDataset(signal, e.value, 'ds_day_depth')
            .then(result => result.json())
            .then(response => {
                if (response.responseCode === 'OK') {
                    if (!response) return

                    if (response.result && response.result.data && response.result.data.length) {
                        this.setState({ tableData: addCalculatedDvDColumns(response.result.data) })
                        this.setState({ copyMinDate: this.state['tableData'][0].timestamp })
                    } else {
                        this.setState({ tableData: addCalculatedDvDColumns(emptyDvDDataset) })
                        this.setState({ copyMinDate: 0 })
                    }
                    this.getAndSetDataStats()
                    this.validateInputs()
                } else {
                    globalNotification.error(response.message ?? "We're having trouble retrieving the dataset")
                    this.setState({ copyMinDate: 0 })
                }
                this.setState({ isDataLoading: false })
            })
            .catch(e => {
                globalNotification.error(e.message ?? "We're having trouble retrieving the dataset")
                this.setState({ isDataLoading: false })
                this.setState({ copyMinDate: 0 })
            })
            .finally(() => this.setState({ isDataLoading: false }))
    }

    parseDate = date => {
        if (!date) return null
        if (moment.isMoment(date)) return +date._d

        const parsed = parseInt(date)

        if (isNaN(parsed)) return null

        return parsed
    }

    onChangeFilterParameters = (dataType: string, name: string, value: number) => {
        this.setState({ [`${name}`]: value }, () => {})
        this.getAndSetDataStats()
        this.validateInputs()
        //Validate
    }

    render() {
        const setFilterParameters = (start?: number, end?: number, depth?: number) => {
            this.setState({
                copyStartDate: start,
                copyEndDate: end,
                copySideTrackDepth: depth
            })
            this.getAndSetDataStats()
            this.validateInputs()
        }

        const getNearestDownDate = searchTime => {
            const data: Array<Record<string, string>> = this.state['tableData']
            let clonedData = _.clone(data) as Array<Record<string, string>>
            clonedData = clonedData.filter(x => parseInt(x['timestamp']) <= searchTime)
            if (clonedData.length > 0) {
                let timeStamps: number[] = clonedData.map(x => parseInt(x['timestamp']))
                timeStamps = timeStamps.sort()
                return timeStamps[timeStamps.length - 1]
            }
            return 0
        }

        const getState = (key: string) => {
            return this.state[key]
        }

        const getPropValue = (key: string) => {
            return this.props[key]
        }

        const highlightingAutocompleteRenderer = (instance, td, row, col, prop, value, cellProperties) => {
            try {
                const t = this.state['tableRef'].current?.hotInstance?.getDataAtCell(row, 2)
                if (prop === 'status' || (value !== undefined && value !== '')) {
                    let container = getElementForCell(row, col)
                    if (prop === 'status') {
                        ReactDOM.render(
                            <div>
                                {t === this.state['copyStartDate'] ? (
                                    <i className="fa fa-arrow-down"></i>
                                ) : t === this.state['copyEndDate'] ? (
                                    <i className="fa fa-arrow-up"></i>
                                ) : (
                                    ''
                                )}
                            </div>,
                            container
                        )
                    } else {
                        ReactDOM.render(<div>{value}</div>, container)
                    }

                    while (td.firstChild) {
                        td.removeChild(td.firstChild)
                    }
                    td.appendChild(container)

                    if (t < this.props.maxDate && t >= this.state['copyMinDate']) {
                        if (
                            this.state['copyStartDate'] &&
                            t >= this.state['copyStartDate'] &&
                            this.state['copyEndDate'] &&
                            t <= this.state['copyEndDate']
                        ) {
                            td.style.background = '#37A3EF'
                            td.style.color = 'white'
                        }
                    } else {
                        td.style.background = '#d3d3d3'
                        td.style.color = 'black'
                    }
                    return td
                }
                td.innerHTML = '<div></div>'
                return td
            } catch {
                console.log('Rendering error')
            }
        }

        return (
            <div>
                <StyledLiveModal className="modal-lg" onHide={this.closeModal} show={true}>
                    <LiveModal.Header>
                        <LiveModal.Title>
                            {this.props.modalTitle ? this.props.modalTitle : i18n('Copy Data from Well')}
                        </LiveModal.Title>
                    </LiveModal.Header>
                    <LiveModal.Body>
                        <FormConfig>
                            <ContentBody>
                                <React.Fragment>
                                    <Container>
                                        <section>
                                            <header>{i18n('Query Options')}</header>
                                            <div className="form-group">
                                                <Field>
                                                    <label>
                                                        <span>{i18n('Well')}</span>
                                                    </label>
                                                    <Select
                                                        isSearchable={true}
                                                        isClearable={true}
                                                        placeholder="Search wells..."
                                                        onChange={e => this.onChangeWellSelect(e)}
                                                        options={this.state['wells']}
                                                        value={this.state['copyWellId']}
                                                        noOptionsMessage={() => 'No wells found'}
                                                        isLoading={this.state['isDropDownLoading']}
                                                    />
                                                </Field>
                                                <br></br>
                                                <ContainerDate>
                                                    <Field style={{ zIndex: 104 }}>
                                                        <label>
                                                            <span>{i18n('Start date')}</span>
                                                        </label>
                                                        <InputContainerDatepickerStyled>
                                                            <DatePicker
                                                                disabled={!this.state['copyWellId']}
                                                                minDate={moment(this.state['copyMinDate'])}
                                                                maxDate={moment(this.props.maxDate)}
                                                                selected={
                                                                    this.state['copyStartDate']
                                                                        ? moment(this.state['copyStartDate'])
                                                                        : undefined
                                                                }
                                                                customInput={<CustomDateTimeInput />}
                                                                dateFormat={dateService.getDateTimeFormat()}
                                                                placeholderText={dateService
                                                                    .getDateTimeFormat()
                                                                    .toLowerCase()}
                                                                fixedHeight
                                                                onChange={mom => {
                                                                    const dt = new Date(mom).getTime()
                                                                    this.onChangeFilterParameters(
                                                                        'datetime',
                                                                        'copyStartDate',
                                                                        dt
                                                                    )
                                                                    return dt
                                                                }}
                                                                showTimeSelect
                                                                yearDropdownItemNumber={5}
                                                                menuPortalTarget={document.body}
                                                            />
                                                        </InputContainerDatepickerStyled>
                                                    </Field>
                                                    <Field style={{ marginRight: '20px', zIndex: 104 }}>
                                                        <label>
                                                            <span>{i18n('End date')}</span>
                                                        </label>
                                                        <InputContainerDatepickerStyled>
                                                            <DatePicker
                                                                disabled={!this.state['copyWellId']}
                                                                minDate={moment(this.state['copyMinDate'])}
                                                                maxDate={moment(this.props.maxDate)}
                                                                selected={
                                                                    this.state['copyEndDate']
                                                                        ? moment(this.state['copyEndDate'])
                                                                        : undefined
                                                                }
                                                                customInput={<CustomDateTimeInput />}
                                                                dateFormat={dateService.getDateTimeFormat()}
                                                                placeholderText={dateService
                                                                    .getDateTimeFormat()
                                                                    .toLowerCase()}
                                                                fixedHeight
                                                                showTimeSelect
                                                                yearDropdownItemNumber={5}
                                                                onChange={mom => {
                                                                    const dt = new Date(mom).getTime()
                                                                    this.onChangeFilterParameters(
                                                                        'datetime',
                                                                        'copyEndDate',
                                                                        dt
                                                                    )
                                                                    return dt
                                                                }}
                                                            />
                                                        </InputContainerDatepickerStyled>
                                                    </Field>
                                                    <Field>
                                                        <label>
                                                            <span>{i18n('Side Track Bit Depth')}</span>
                                                        </label>
                                                        <InputContainerDatepickerStyled>
                                                            <FormGroup
                                                                type={'number'}
                                                                name={'copySideTrackDepth'}
                                                                placeholder={'Side Track Bit Depth'}
                                                                value={this.state['copySideTrackDepth']}
                                                                onChange={undefined}
                                                                disabled={true}
                                                            />
                                                        </InputContainerDatepickerStyled>
                                                    </Field>
                                                </ContainerDate>
                                            </div>
                                        </section>
                                        <section>
                                            <header>
                                                {i18n('Data')}
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        float: 'right',
                                                        fontStyle: 'italic',
                                                        fontSize: '12px',
                                                        fontWeight: 'normal',
                                                        color: 'rgb(55, 163, 239)'
                                                    }}
                                                >
                                                    {i18n(this.getDataStatsAsString())}
                                                </div>
                                            </header>
                                            {this.state['tableData'] && this.state['tableData'].length > 0 ? (
                                                <StyledOverlay>
                                                    {this.state['isDataLoading'] && (
                                                        <StyledOverlay className="inner" style={{ height: 400 }}>
                                                            <LoadingSpinner message="Loading..." />
                                                        </StyledOverlay>
                                                    )}
                                                    <HotContainer>
                                                        <HotTable
                                                            ref={this.state['tableRef']}
                                                            data={this.state['tableData']}
                                                            settings={{
                                                                rowHeaders: true,
                                                                readOnlyCellClassName: 'is-readOnly',
                                                                columns: [
                                                                    {
                                                                        data: 'status',
                                                                        readOnly: true,
                                                                        renderer: highlightingAutocompleteRenderer,
                                                                        width: '20px'
                                                                    },
                                                                    {
                                                                        data: 'datetime',
                                                                        readOnly: true,
                                                                        renderer: highlightingAutocompleteRenderer
                                                                    },
                                                                    {
                                                                        data: 'timestamp',
                                                                        readOnly: true,
                                                                        renderer: highlightingAutocompleteRenderer
                                                                    },
                                                                    {
                                                                        data: 'bit_depth_ft',
                                                                        readOnly: true,
                                                                        renderer: highlightingAutocompleteRenderer
                                                                    },
                                                                    {
                                                                        data: 'hole_depth_ft',
                                                                        readOnly: true,
                                                                        renderer: highlightingAutocompleteRenderer
                                                                    }
                                                                ],
                                                                colHeaders: [
                                                                    '',
                                                                    'Date/Time',
                                                                    'Timestamp',
                                                                    'Bit Depth (ft)',
                                                                    'Hole Depth (ft)'
                                                                ],
                                                                columnSorting: {
                                                                    sortEmptyCells: true,
                                                                    initialConfig: {
                                                                        column: 1,
                                                                        sortOrder: 'asc'
                                                                    }
                                                                },
                                                                stretchH: 'all',
                                                                autoRowSize: true,
                                                                contextMenu: {
                                                                    callback(key, selection, clickEvent) {},
                                                                    items: {
                                                                        auto_select_range: {
                                                                            name: 'Auto-select valid range',
                                                                            callback(key, selection) {
                                                                                setFilterParameters(
                                                                                    getState('copyMinDate'),
                                                                                    getNearestDownDate(
                                                                                        getPropValue('maxDate') - 1
                                                                                    ),
                                                                                    0
                                                                                )
                                                                            },
                                                                            disabled: function () {
                                                                                let disable = true
                                                                                const selectionRows = this.getSelectedRangeLast()
                                                                                const selectStartTime = this.getDataAtCell(
                                                                                    selectionRows.from.row,
                                                                                    2
                                                                                )

                                                                                if (
                                                                                    selectStartTime >=
                                                                                        getState('copyMinDate') &&
                                                                                    selectStartTime <
                                                                                        getPropValue('maxDate')
                                                                                ) {
                                                                                    disable = false
                                                                                }
                                                                                return disable
                                                                            }
                                                                        },
                                                                        get_range_rows: {
                                                                            name: '<b>Get Range from Selected Rows</b>',
                                                                            callback(key, selection) {
                                                                                const startTime = this.getDataAtCell(
                                                                                    selection[0].start.row,
                                                                                    2
                                                                                )
                                                                                const endTime = this.getDataAtCell(
                                                                                    selection[0].end.row,
                                                                                    2
                                                                                )
                                                                                const sideTrackDepth = this.getDataAtCell(
                                                                                    selection[0].end.row,
                                                                                    3
                                                                                )
                                                                                setFilterParameters(
                                                                                    startTime,
                                                                                    endTime,
                                                                                    sideTrackDepth
                                                                                )
                                                                            },
                                                                            disabled: function () {
                                                                                let disable = true
                                                                                const selectionRows = this.getSelectedRangeLast()
                                                                                const selectStartTime = this.getDataAtCell(
                                                                                    selectionRows.from.row,
                                                                                    2
                                                                                )
                                                                                const selectEndTime = this.getDataAtCell(
                                                                                    selectionRows.to.row,
                                                                                    2
                                                                                )

                                                                                if (
                                                                                    selectStartTime >=
                                                                                        getState('copyMinDate') &&
                                                                                    selectStartTime <
                                                                                        getPropValue('maxDate') &&
                                                                                    selectEndTime >=
                                                                                        getState('copyMinDate') &&
                                                                                    selectEndTime <
                                                                                        getPropValue('maxDate')
                                                                                ) {
                                                                                    disable = false
                                                                                }
                                                                                return (
                                                                                    selectionRows.from.row ===
                                                                                        selectionRows.to.row || disable
                                                                                )
                                                                            }
                                                                        },
                                                                        seperatorOne: '---------',
                                                                        setResetStart: {
                                                                            name: getState('copyStartDate')
                                                                                ? 'Reset Start Date'
                                                                                : 'Set Start Date',
                                                                            callback(key, selection) {
                                                                                if (getState('copyStartDate')) {
                                                                                    setFilterParameters(
                                                                                        undefined,
                                                                                        getState('copyEndDate'),
                                                                                        getState('copySideTrackDepth')
                                                                                    )
                                                                                } else {
                                                                                    const startTime = this.getDataAtCell(
                                                                                        selection[0].start.row,
                                                                                        2
                                                                                    )
                                                                                    if (
                                                                                        getState('copyEndDate') &&
                                                                                        startTime >
                                                                                            getState('copyEndDate')
                                                                                    ) {
                                                                                        globalNotification.error(
                                                                                            'Choose a start date that falls before the end date!'
                                                                                        )
                                                                                    } else {
                                                                                        setFilterParameters(
                                                                                            startTime,
                                                                                            getState('copyEndDate'),
                                                                                            getState(
                                                                                                'copySideTrackDepth'
                                                                                            )
                                                                                        )
                                                                                    }
                                                                                }
                                                                            },
                                                                            disabled: function () {
                                                                                const selectionRows = this.getSelectedRangeLast()
                                                                                let disable = true
                                                                                const selectedTime = this.getDataAtCell(
                                                                                    selectionRows.from.row,
                                                                                    2
                                                                                )
                                                                                if (
                                                                                    selectedTime >=
                                                                                        getState('copyMinDate') &&
                                                                                    selectedTime <
                                                                                        getPropValue('maxDate')
                                                                                ) {
                                                                                    disable = false
                                                                                }

                                                                                return (
                                                                                    selectionRows.from.row !==
                                                                                        selectionRows.to.row || disable
                                                                                )
                                                                            }
                                                                        },
                                                                        setResetEnd: {
                                                                            name: getState('copyEndDate')
                                                                                ? 'Reset End Date'
                                                                                : 'Set End Date',
                                                                            callback(key, selection) {
                                                                                if (getState('copyEndDate')) {
                                                                                    setFilterParameters(
                                                                                        getState('copyStartDate'),
                                                                                        undefined,
                                                                                        undefined
                                                                                    )
                                                                                } else {
                                                                                    const endTime = this.getDataAtCell(
                                                                                        selection[0].start.row,
                                                                                        2
                                                                                    )
                                                                                    const sideTrackDepth = this.getDataAtCell(
                                                                                        selection[0].start.row,
                                                                                        3
                                                                                    )
                                                                                    if (
                                                                                        getState('copyStartDate') &&
                                                                                        endTime <
                                                                                            getState('copyStartDate')
                                                                                    ) {
                                                                                        globalNotification.error(
                                                                                            'Choose a end date that falls after the start date!'
                                                                                        )
                                                                                    } else {
                                                                                        setFilterParameters(
                                                                                            getState('copyStartDate'),
                                                                                            endTime,
                                                                                            sideTrackDepth
                                                                                        )
                                                                                    }
                                                                                }
                                                                            },
                                                                            disabled: function () {
                                                                                const selectionRows = this.getSelectedRangeLast()
                                                                                let disable = true
                                                                                const selectedTime = this.getDataAtCell(
                                                                                    selectionRows.from.row,
                                                                                    2
                                                                                )
                                                                                if (
                                                                                    selectedTime >=
                                                                                        getState('copyMinDate') &&
                                                                                    selectedTime <
                                                                                        getPropValue('maxDate')
                                                                                ) {
                                                                                    disable = false
                                                                                }
                                                                                return (
                                                                                    selectionRows.from.row !==
                                                                                        selectionRows.to.row || disable
                                                                                )
                                                                            }
                                                                        },
                                                                        resetAll: {
                                                                            name: 'Reset All',
                                                                            callback(key, selection) {
                                                                                setFilterParameters(
                                                                                    undefined,
                                                                                    undefined,
                                                                                    undefined
                                                                                )
                                                                            },
                                                                            disabled: function () {
                                                                                return !(
                                                                                    getState('copyEndDate') !=
                                                                                        undefined ||
                                                                                    getState('copyStartDate') !=
                                                                                        undefined
                                                                                )
                                                                            }
                                                                        }
                                                                    }
                                                                },

                                                                height: 400
                                                            }}
                                                        ></HotTable>
                                                    </HotContainer>
                                                </StyledOverlay>
                                            ) : (
                                                <div>Select Well to show data</div>
                                            )}
                                        </section>
                                    </Container>
                                </React.Fragment>
                            </ContentBody>
                        </FormConfig>
                    </LiveModal.Body>
                    <LiveModal.Footer>
                        <ButtonWithLoading
                            className={`btn accept btn- mr-5`}
                            loading={this.state['isDataLoading']}
                            buttonMessage={'Copy Data'}
                            buttonMessageLoading={'Copying'}
                            onClick={() => {
                                this.copyData()
                            }}
                            disabled={!this.state['isGoodInputs']}
                        />

                        {!this.state['isDataLoading'] ? (
                            <span>
                                <span>{i18n('or')}</span>
                                <a
                                    className="cancel"
                                    onClick={() => {
                                        this.closeModal()
                                    }}
                                >
                                    {' '}
                                    {i18n('cancel')}
                                </a>
                            </span>
                        ) : null}
                    </LiveModal.Footer>
                </StyledLiveModal>
            </div>
        )
    }
}

export default DvDCopyDataForm
